import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Typography,
  IconButton,
  LinearProgress,
  Stack,
  Alert,
  Grid,
} from '@mui/material';
import {
  CloudUpload,
  Close,
  Delete,
  CheckCircle,
  Error as ErrorIcon,
  Schedule,
} from '@mui/icons-material';
import { FileUploadState } from './types';
import { SupportMaterialService } from './supportMaterialService';

interface UploadModalProps {
  open: boolean;
  onClose: () => void;
  folders: string[];
  onUploadComplete: () => void;
  onNotify: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

export const UploadModal: React.FC<UploadModalProps> = ({
  open,
  onClose,
  folders,
  onUploadComplete,
  onNotify,
}) => {
  const [selectedFolder, setSelectedFolder] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [uploadFiles, setUploadFiles] = useState<FileUploadState[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadStartTime, setUploadStartTime] = useState<number | null>(null);

  // Reset cuando se cierra el modal
  useEffect(() => {
    if (!open) {
      setUploadFiles([]);
      setSelectedFolder('');
      setNewFolderName('');
      setUploading(false);
      setUploadStartTime(null);
    }
  }, [open]);

  // ==================== HANDLERS ====================

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    if (files.length === 0) return;

    const folder = newFolderName || selectedFolder;
    
    if (!folder) {
      onNotify('Selecciona o crea una carpeta primero', 'warning');
      return;
    }

    console.log('[UploadModal] Archivos seleccionados:', files.length);
    
    const newFiles: FileUploadState[] = files.map((file, i) => {
      console.log(`  [${i + 1}/${files.length}]`, file.name, `(${formatFileSize(file.size)})`);
      return {
        id: `${Date.now()}-${i}`,
        file,
        fileName: file.name,
        folder,
        status: 'pending',
        progress: 0,
      };
    });

    setUploadFiles((prev) => [...prev, ...newFiles]);
  };

  const removeFile = (id: string) => {
    setUploadFiles((prev) => prev.filter((f) => f.id !== id));
    console.log('[UploadModal] Archivo removido de la lista');
  };

  const handleUploadAll = async () => {
    if (uploadFiles.length === 0) {
      onNotify('No hay archivos para subir', 'warning');
      return;
    }

    const folder = newFolderName || selectedFolder;
    if (!folder) {
      onNotify('Selecciona o crea una carpeta', 'warning');
      return;
    }

    console.log('========================================');
    console.log('[UploadModal] INICIO DEL PROCESO DE UPLOAD');
    console.log('[UploadModal] Carpeta destino:', folder);
    console.log('[UploadModal] Total de archivos:', uploadFiles.length);
    console.log('========================================');

    setUploading(true);
    setUploadStartTime(Date.now());

    try {
      const username = localStorage.getItem('username') || 'admin';
      console.log('[UploadModal] Usuario:', username);

      // PASO 1: Solicitar URLs firmadas para todos los archivos
      console.log('\n[UploadModal] PASO 1: Solicitando URLs firmadas del backend...');
      
      const uploadRequests = uploadFiles.map((fileObj) => ({
        fileName: fileObj.fileName,
        folder: fileObj.folder,
        metadata: {
          createdBy: username,
        },
      }));

      console.log('[UploadModal] Requests preparados:', uploadRequests.length);

      const uploadResponses = await SupportMaterialService.getMultipleUploadUrls(uploadRequests);
      
      console.log('[UploadModal] ✓ URLs firmadas recibidas:', uploadResponses.length);
      
      // Validar que todas las URLs se generaron correctamente
      const invalidResponses = uploadResponses.filter(r => !r.uploadUrl);
      if (invalidResponses.length > 0) {
        console.error('[UploadModal] ✗ URLs inválidas recibidas:', invalidResponses);
        throw new Error(`${invalidResponses.length} archivos no obtuvieron URL válida`);
      }

      // PASO 2: Subir archivos en paralelo a S3
      console.log('\n[UploadModal] PASO 2: Subiendo archivos a S3 en paralelo...');
      console.log('[UploadModal] Iniciando', uploadFiles.length, 'uploads simultáneos');

      const uploadPromises = uploadFiles.map(async (fileObj, index) => {
        const { uploadUrl, fileName: responseFileName } = uploadResponses[index];
        const uploadNumber = index + 1;
        const uploadTotal = uploadFiles.length;

        console.log(`\n[UploadModal] → Iniciando upload ${uploadNumber}/${uploadTotal}: ${fileObj.fileName}`);

        try {
          // Actualizar estado a 'uploading'
          setUploadFiles((prev) =>
            prev.map((f) =>
              f.id === fileObj.id ? { ...f, status: 'uploading' } : f
            )
          );

          const fileStartTime = Date.now();

          // Subir a S3 con progress tracking
          // CRÍTICO: Pasar metadata para que coincida con la presigned URL
          await SupportMaterialService.uploadToS3(
            uploadUrl,
            fileObj.file,
            (progress: number) => {
              setUploadFiles((prev) =>
                prev.map((f) =>
                  f.id === fileObj.id ? { ...f, progress } : f
                )
              );
            },
            { createdBy: username } // ← Metadata debe coincidir con la URL
          );

          const fileDuration = ((Date.now() - fileStartTime) / 1000).toFixed(2);

          // Marcar como exitoso
          setUploadFiles((prev) =>
            prev.map((f) =>
              f.id === fileObj.id
                ? { ...f, status: 'success', progress: 100 }
                : f
            )
          );

          console.log(`[UploadModal] ✓ Upload ${uploadNumber}/${uploadTotal} completado: ${fileObj.fileName} (${fileDuration}s)`);

        } catch (error: any) {
          console.error(`[UploadModal] ✗ Upload ${uploadNumber}/${uploadTotal} falló:`, {
            fileName: fileObj.fileName,
            error: error.message,
            stack: error.stack,
          });

          // Marcar como error
          setUploadFiles((prev) =>
            prev.map((f) =>
              f.id === fileObj.id
                ? { ...f, status: 'error', error: error.message }
                : f
            )
          );
          throw error;
        }
      });

      // Esperar a que todos terminen (exitosamente o con error)
      const results = await Promise.allSettled(uploadPromises);

      const totalDuration = uploadStartTime 
        ? ((Date.now() - uploadStartTime) / 1000).toFixed(2)
        : '0';

      // Analizar resultados
      const successCount = results.filter((r) => r.status === 'fulfilled').length;
      const errorCount = results.filter((r) => r.status === 'rejected').length;

      console.log('\n========================================');
      console.log('[UploadModal] RESUMEN DEL UPLOAD:');
      console.log(`  Total archivos:    ${uploadFiles.length}`);
      console.log(`  Exitosos:          ${successCount} ✓`);
      console.log(`  Fallidos:          ${errorCount} ✗`);
      console.log(`  Tiempo total:      ${totalDuration}s`);
      console.log('========================================\n');

      // Mostrar notificación según resultado
      if (errorCount === 0) {
        // Todos exitosos
        onNotify(
          `${successCount} archivo(s) subido(s) exitosamente en ${totalDuration}s`,
          'success'
        );

        // Esperar un momento para que el usuario vea el resultado
        setTimeout(() => {
          onUploadComplete();
          onClose();
        }, 1500);

      } else if (successCount > 0) {
        // Algunos exitosos, algunos fallidos
        onNotify(
          `${successCount} archivo(s) subido(s), ${errorCount} fallaron`,
          'warning'
        );
      } else {
        // Todos fallaron
        onNotify('Error al subir archivos', 'error');
      }

    } catch (error: any) {
      console.error('[UploadModal] ✗ ERROR GENERAL:', error);
      onNotify(error.message || 'Error al subir archivos', 'error');
    } finally {
      setUploading(false);
    }
  };

  // ==================== COMPUTED ====================

  const folderValue = newFolderName || selectedFolder;
  const canUpload = uploadFiles.length > 0 && folderValue && !uploading;

  const successCount = uploadFiles.filter((f) => f.status === 'success').length;
  const errorCount = uploadFiles.filter((f) => f.status === 'error').length;
  const uploadingCount = uploadFiles.filter((f) => f.status === 'uploading').length;

  // ==================== RENDER ====================

  return (
    <Dialog
      open={open}
      onClose={uploading ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Subir Material de Apoyo</Typography>
          <IconButton onClick={onClose} disabled={uploading}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Selector de carpeta */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Carpeta existente</InputLabel>
                <Select
                  value={selectedFolder}
                  onChange={(e) => {
                    setSelectedFolder(e.target.value);
                    setNewFolderName('');
                    setUploadFiles((prev) =>
                      prev.map((f) => ({ ...f, folder: e.target.value }))
                    );
                  }}
                  label="Carpeta existente"
                  disabled={uploading || !!newFolderName}
                >
                  <MenuItem value="">— Seleccionar —</MenuItem>
                  {folders.map((f) => (
                    <MenuItem key={f} value={f}>
                      {f}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="O crear nueva carpeta"
                value={newFolderName}
                onChange={(e) => {
                  const value = e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, '_');
                  setNewFolderName(value);
                  setSelectedFolder('');
                  setUploadFiles((prev) =>
                    prev.map((f) => ({ ...f, folder: value }))
                  );
                }}
                placeholder="Ej: manuales, imagenes"
                disabled={uploading || !!selectedFolder}
              />
            </Grid>
          </Grid>

          {/* Zona de drop */}
          <Paper
            variant="outlined"
            sx={{
              p: 6,
              textAlign: 'center',
              borderStyle: 'dashed',
              borderWidth: 2,
              borderColor: folderValue ? 'primary.main' : 'grey.300',
              cursor: folderValue ? 'pointer' : 'not-allowed',
              opacity: folderValue ? 1 : 0.5,
              '&:hover': folderValue
                ? {
                    borderColor: 'primary.dark',
                    bgcolor: 'action.hover',
                  }
                : {},
            }}
            component="label"
          >
            <input
              type="file"
              multiple
              hidden
              disabled={!folderValue || uploading}
              onChange={handleFileSelect}
            />
            <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Arrastra archivos o haz clic para seleccionar
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {folderValue
                ? 'Todos los tipos de archivo soportados'
                : 'Primero selecciona o crea una carpeta'}
            </Typography>
          </Paper>

          {/* Resumen de progreso */}
          {uploading && uploadFiles.length > 0 && (
            <Alert severity="info" icon={<Schedule />}>
              <Typography variant="body2" fontWeight={500}>
                Subiendo {uploadingCount} de {uploadFiles.length} archivos...
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Exitosos: {successCount} • Fallidos: {errorCount}
                {uploadStartTime && ` • Tiempo: ${((Date.now() - uploadStartTime) / 1000).toFixed(0)}s`}
              </Typography>
            </Alert>
          )}

          {/* Lista de archivos */}
          {uploadFiles.length > 0 && (
            <Box>
              <Typography variant="subtitle1" gutterBottom fontWeight={500}>
                Archivos seleccionados ({uploadFiles.length})
              </Typography>

              <Stack spacing={1.5} sx={{ maxHeight: 300, overflowY: 'auto', pr: 1 }}>
                {uploadFiles.map((fileObj) => (
                  <Paper
                    key={fileObj.id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      bgcolor:
                        fileObj.status === 'success'
                          ? 'success.50'
                          : fileObj.status === 'error'
                          ? 'error.50'
                          : fileObj.status === 'uploading'
                          ? 'info.50'
                          : 'background.paper',
                      border: '1px solid',
                      borderColor:
                        fileObj.status === 'success'
                          ? 'success.main'
                          : fileObj.status === 'error'
                          ? 'error.main'
                          : fileObj.status === 'uploading'
                          ? 'info.main'
                          : 'divider',
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      {/* Status icon */}
                      {fileObj.status === 'success' && (
                        <CheckCircle color="success" />
                      )}
                      {fileObj.status === 'error' && <ErrorIcon color="error" />}
                      {fileObj.status === 'uploading' && (
                        <Schedule color="info" />
                      )}

                      {/* File info */}
                      <Box flex={1}>
                        <Typography variant="body2" fontWeight={500}>
                          {fileObj.fileName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatFileSize(fileObj.file.size)} • {fileObj.folder}
                        </Typography>

                        {/* Progress bar */}
                        {fileObj.status === 'uploading' && (
                          <Box sx={{ mt: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={fileObj.progress}
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mt: 0.5, display: 'block' }}
                            >
                              Subiendo... {fileObj.progress}%
                            </Typography>
                          </Box>
                        )}

                        {/* Success message */}
                        {fileObj.status === 'success' && (
                          <Typography variant="caption" color="success.main" sx={{ mt: 0.5 }}>
                            ✓ Subido exitosamente
                          </Typography>
                        )}

                        {/* Error message */}
                        {fileObj.status === 'error' && fileObj.error && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                            ✗ {fileObj.error}
                          </Typography>
                        )}
                      </Box>

                      {/* Delete button */}
                      {fileObj.status === 'pending' && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeFile(fileObj.id)}
                          disabled={uploading}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Paper>
                ))}
              </Stack>
            </Box>
          )}

          {/* Info/Warning messages */}
          {uploadFiles.length === 0 && folderValue && (
            <Alert severity="info">
              Selecciona archivos para subir a la carpeta "{folderValue}"
            </Alert>
          )}

          {/* Debug info (solo en desarrollo) */}
          {process.env.NODE_ENV === 'development' && uploadFiles.length > 0 && (
            <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
              <strong>Debug Info:</strong> Revisa la consola del navegador (F12) para ver logs detallados del proceso de upload.
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={uploading}>
          {uploading ? 'Cerrará al terminar' : 'Cancelar'}
        </Button>
        <Button
          variant="contained"
          startIcon={uploading ? <Schedule /> : <CloudUpload />}
          onClick={handleUploadAll}
          disabled={!canUpload}
        >
          {uploading 
            ? `Subiendo (${uploadingCount}/${uploadFiles.length})...`
            : `Subir ${uploadFiles.length > 0 ? `(${uploadFiles.length})` : ''}`
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ==================== HELPERS ====================

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};
